import { BaseObj } from '@/objects/BaseObj';
import { useBaseObjEntityStateHandler } from '@/objects/entitystatehandler/entitystatehandler';
import { isRef, ref, unref } from 'vue';
import type { EntityState } from './../enums/EntityState';

export class MatrixShootingCellWarningsAcknowledgement extends BaseObj {
    public ID: number;
    public __id: number;

    public AcknowledgedByName: string | null;
    public __acknowledgedbyname: string | null;

    public AcknowledgedByUserId: number;
    public __acknowledgedbyuserid: number;

    public LapNumber: number;
    public __lapnumber: number;

    public RaceContestantId: number;
    public __racecontestantid: number;

    public StationRoleId: number;
    public __stationroleid: number;

    public __entityStateHandler;
    public __selfEntityState: EntityState;
    public __entityState: any;
    public get EntityState(): EntityState {
        return unref(this.__entityState);
    }
    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        ID?: number;
        AcknowledgedByName?: string | null;
        AcknowledgedByUserId?: number;
        EntityState?: EntityState;
        LapNumber?: number;
        RaceContestantId?: number;
        StationRoleId?: number;
    }) {
        super();
        this.__entityState = ref(values.EntityState ?? 'Unchanged');
        this.__selfEntityState = values.EntityState ?? 'Unchanged';
        this.__entityStateHandler = useBaseObjEntityStateHandler<MatrixShootingCellWarningsAcknowledgement>(
            unref(this.__entityState),
            (states) => {
                if (isRef(this.__entityState)) {
                    this.__entityState.value = states.EntityStateWithChildrenConsidered;
                } else {
                    console.warn(
                        'MatrixShootingCellWarningsAcknowledgement tried to set entitystate, but entitystate is not a ref! This should never happen',
                    );
                }
                this.__selfEntityState = states.SelfEntityState;
            },
        );
        this.ID = values.ID ?? 0;
        this.__id = values.ID ?? 0;
        Object.defineProperty(this, 'ID', {
            get(): number {
                return this.__id;
            },
            enumerable: true,
            configurable: true,
        });

        this.AcknowledgedByName = values.AcknowledgedByName ?? null;
        this.__acknowledgedbyname = values.AcknowledgedByName ?? null;
        Object.defineProperty(this, 'AcknowledgedByName', {
            get(): string | null {
                return this.__acknowledgedbyname;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('AcknowledgedByName', this.__acknowledgedbyname, value);
                this.__acknowledgedbyname = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.AcknowledgedByUserId = values.AcknowledgedByUserId ?? 0;
        this.__acknowledgedbyuserid = values.AcknowledgedByUserId ?? 0;
        Object.defineProperty(this, 'AcknowledgedByUserId', {
            get(): number {
                return this.__acknowledgedbyuserid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('AcknowledgedByUserId', this.__acknowledgedbyuserid, value);
                this.__acknowledgedbyuserid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.LapNumber = values.LapNumber ?? 0;
        this.__lapnumber = values.LapNumber ?? 0;
        Object.defineProperty(this, 'LapNumber', {
            get(): number {
                return this.__lapnumber;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('LapNumber', this.__lapnumber, value);
                this.__lapnumber = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.RaceContestantId = values.RaceContestantId ?? 0;
        this.__racecontestantid = values.RaceContestantId ?? 0;
        Object.defineProperty(this, 'RaceContestantId', {
            get(): number {
                return this.__racecontestantid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('RaceContestantId', this.__racecontestantid, value);
                this.__racecontestantid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.StationRoleId = values.StationRoleId ?? 0;
        this.__stationroleid = values.StationRoleId ?? 0;
        Object.defineProperty(this, 'StationRoleId', {
            get(): number {
                return this.__stationroleid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('StationRoleId', this.__stationroleid, value);
                this.__stationroleid = value;
            },
            enumerable: true,
            configurable: true,
        });
    }

    public _setNotifyParentOfModificationCallback(callback: () => void) {
        this.__entityStateHandler.setNotifyParentOfModificationCallback(callback);
    }

    public serialize(): string {
        return (
            '{' +
            `"ID": ${JSON.stringify(this.ID)}` +
            ',' +
            `"AcknowledgedByName": ${JSON.stringify(this.AcknowledgedByName)}` +
            ',' +
            `"AcknowledgedByUserId": ${JSON.stringify(this.AcknowledgedByUserId)}` +
            ',' +
            `"EntityState": ${unref(this.__selfEntityState) == 'Unchanged' ? 0 : unref(this.__selfEntityState) == 'Deleted' ? 1 : unref(this.__selfEntityState) == 'Modified' ? 2 : unref(this.__selfEntityState) == 'Added' ? 3 : 0}` +
            ',' +
            `"LapNumber": ${JSON.stringify(this.LapNumber)}` +
            ',' +
            `"RaceContestantId": ${JSON.stringify(this.RaceContestantId)}` +
            ',' +
            `"StationRoleId": ${JSON.stringify(this.StationRoleId)}` +
            ',' +
            `"ChangedColumns": ${unref(this.__selfEntityState) !== 'Modified' ? 'null' : JSON.stringify(this.__entityStateHandler.getChangedPropertiesArray())}` +
            '}'
        );
    }

    /**
     * Sets the entity state to Deleted
     */
    public Delete() {
        if (this.__selfEntityState === 'Added') {
            throw new Error('Cannot delete an object that is in Added state. It has not been saved yet.');
        }
        this.__selfEntityState = 'Deleted';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Undeletes the object by setting resetting the EntityState
     *
     * If the object has modifications, the new state will be 'Modified', otherwise 'Unchanged'
     */
    public DeleteUndo() {
        this.__selfEntityState = 'Unchanged';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Sets the entity state to Added
     *
     * Also sets the ID to the supplied ID. If no id parameter supplied, the previous id is kept
     *
     * Note: This does not do anything with the children, so use with caution
     *
     * The use case of this is to make copies of existing objects as new objects without having to fill every field.
     */
    public SetEntityStateAsAddedAndResetId(id?: number) {
        if (this.EntityState !== 'Unchanged') {
            throw new Error('Changing a modified object to Added is not supported.');
        }
        this.__selfEntityState = 'Added';
        if (id !== undefined) {
            this.__id = id;
        }
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Creates an instance of MatrixShootingCellWarningsAcknowledgement from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        ID?: number;
        AcknowledgedByName?: string | null;
        AcknowledgedByUserId?: number;
        LapNumber?: number;
        RaceContestantId?: number;
        StationRoleId?: number;
        EntityState?: 'Added' | 'Unchanged';
    }): MatrixShootingCellWarningsAcknowledgement {
        return new MatrixShootingCellWarningsAcknowledgement(values);
    }

    public static _FromServerJson(json: any): MatrixShootingCellWarningsAcknowledgement {
        return new MatrixShootingCellWarningsAcknowledgement({
            ID: json.ID,
            AcknowledgedByName: json.AcknowledgedByName ?? '',
            AcknowledgedByUserId: json.AcknowledgedByUserId,
            LapNumber: json.LapNumber,
            RaceContestantId: json.RaceContestantId,
            StationRoleId: json.StationRoleId,
            EntityState: 'Unchanged',
        });
    }

    /**
     * Creates an instance of MatrixShootingCellWarningsAcknowledgement from another instance of MatrixShootingCellWarningsAcknowledgement.
     * @param other The MatrixShootingCellWarningsAcknowledgement you want to clone
     * @returns A cloned MatrixShootingCellWarningsAcknowledgement
     */
    public static FromOther(other: MatrixShootingCellWarningsAcknowledgement): MatrixShootingCellWarningsAcknowledgement {
        if (other.EntityState !== 'Unchanged') {
            throw new Error('Cloning an object with EntityState other than Unchanged is not supported');
        }
        return MatrixShootingCellWarningsAcknowledgement._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return MatrixShootingCellWarningsAcknowledgement.FromOther(this);
    }

    /**
     * IsPropertyChanged.
     */
    public IsPropertyChanged(
        ...props: ('ID' | 'AcknowledgedByName' | 'AcknowledgedByUserId' | 'LapNumber' | 'RaceContestantId' | 'StationRoleId')[]
    ) {
        return this.__entityStateHandler.isPropertyChanged(...props);
    }
    static NAME_OF_CLASS = 'MatrixShootingCellWarningsAcknowledgement';
}
